// @givmo/org-console — WHAT A LEVEL GIVES in one app, said under the level that is chosen (OM-94).
//
// ⚠ IT FOLLOWS THE LEVEL. The line under an invite's app used to say the same thing at Member and at
// Owner, so choosing Owner changed nothing on screen (Jonathan, prod, 2026-10-24). It now lists what
// the chosen level lets the person do there, and changes the moment the level does. The invite says
// it on both doors, and the access editor under each app's level while its permissions are the
// level's own (D-CPO-77 item 3).
//
// ⚠ THE WORDS ARE THE CATALOG'S, NEVER A SECOND DESCRIPTION. Each item is one of the app's own
// permission strings in the host's words (`vocab.permissionLabel`, the labels the access editor's
// Customize list already shows), so the line is true for every app in both classes and cannot drift
// from what the server grants. At Owner it lists what Owner adds, after "everything a Member can":
// the difference is the choice being made. The leads are `TEAM_COPY`'s, as ruled.

import { Check } from 'lucide-react';
import { Icon, testIdProp } from '@givmo/design-system';
import type { OrgConsoleConfig } from './org-console-config';
import { LEVEL_MEMBER, LEVEL_OWNER, keysAtLevel } from './org-access-model';
import { TEAM_COPY } from './org-team-copy';
import { ROLE_OWNER, type OrgTier } from './org-tiers';
import type { OrgModuleCatalogRow } from './wire-types';

export interface LevelGivesProps {
  config: OrgConsoleConfig;
  module: string;
  tier: OrgTier;
  /** The app's catalog row; absent while the catalog is still being read, and then nothing renders. */
  entry: OrgModuleCatalogRow | undefined;
  testId: string;
}

/** The lead line and the items under it, for one app at one level. Pure, so a test can read it. */
export function levelGives(
  appLabel: string,
  tier: OrgTier,
  entry: OrgModuleCatalogRow,
  permissionLabel: (key: string) => string,
): { lead: string; items: string[] } {
  const member = keysAtLevel(entry, LEVEL_MEMBER).map(permissionLabel);
  if (tier !== ROLE_OWNER) {
    // An app whose Member level carries nothing (Connected AI) says so: a Member of it can do nothing.
    return member.length > 0
      ? { lead: TEAM_COPY.memberCan, items: member }
      : { lead: TEAM_COPY.memberNothing(appLabel), items: [] };
  }
  const owner = keysAtLevel(entry, LEVEL_OWNER).map(permissionLabel);
  if (member.length === 0) return { lead: TEAM_COPY.ownerCan, items: owner };
  return owner.length > 0
    ? { lead: TEAM_COPY.ownerAlsoCan, items: owner }
    : { lead: TEAM_COPY.ownerSameAsMember, items: [] };
}

export function LevelGives({ config, module, tier, entry, testId }: LevelGivesProps) {
  if (entry === undefined) return null;
  const { lead, items } = levelGives(
    config.vocab.moduleLabel(module),
    tier,
    entry,
    config.vocab.permissionLabel,
  );
  return (
    <div {...testIdProp(testId)} data-level={tier}>
      <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
        {lead}
      </p>
      {items.length > 0 && (
        <ul className="mt-6 flex flex-col gap-4">
          {items.map((item) => (
            <li
              key={item}
              className="flex items-start gap-8 font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
            >
              <Icon
                icon={Check}
                size={14}
                aria-hidden
                className="mt-2 shrink-0 text-text-tertiary"
              />
              <span className="min-w-0">{item}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
